import React from "react";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import SEO from "@/components/site/SEO";
import { DecorationField, Star } from "@/components/site/Decorations";
import { CheckCircle2 } from "lucide-react";
import { IMG } from "@/lib/siteData";

const goals = [
  "Promote creative and critical thinking",
  "Support language development for all learners including multilingual children",
  "Be culturally responsive",
  "Follow children's lead and interests",
  "Build strong trusting relationships with caregivers",
  "Promote positive self-esteem and independence",
  "Cultivate respect for oneself and others",
  "Encourage positive relationships between children and adults",
  "Facilitate development of healthy habits",
  "Support social, emotional, cognitive and physical growth",
];

export default function GoalsPage() {
  return (
    <div data-testid="goals-page" className="overflow-hidden">
      <SEO
        title="Our Goals"
        description="The Innovators Prep mission: a safe, nurturing, inclusive early childhood education where every child is valued and empowered to reach their fullest potential."
      />
      <PageHero title="Our Goals" crumb="Our Goals" />

      <section className="relative py-20 md:py-28">
        <DecorationField variant="mixed" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <span className="inline-block px-3 py-1 rounded-full bg-brand-green-soft text-brand-green-deep text-xs font-bold tracking-widest uppercase">
              Our Mission
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Innovators Prep <span className="text-brand-green">Mission Statement</span>
            </h2>
            <div className="mt-6 space-y-4 text-gray-600 text-lg leading-relaxed">
              <p>
                The mission of Innovators Prep is to provide a safe, nurturing, inclusive, and
                high-quality early childhood education where every child is valued, supported, and
                empowered to reach their fullest potential.
              </p>
              <p>
                Serving children across our 2K, 3K, Pre-K, and 4410 Special Education Preschool
                programs, we are committed to educating the whole child by supporting cognitive,
                social-emotional, communication, physical, creative, and adaptive development
                through purposeful play, individualized instruction, exploration, and meaningful
                learning experiences.
              </p>
              <p>
                We recognize that every child is unique and develops at their own pace. Our
                dedicated educators, specialists, and support professionals work collaboratively to
                identify and build upon each child's strengths while providing the individualized
                guidance and support needed to promote confidence, independence, curiosity, and a
                lifelong love of learning.
              </p>
              <p>
                At Innovators Prep, inclusion and belonging are fundamental to our mission. We
                celebrate diversity, respect individual differences, and strive to create a school
                community where children of all abilities and backgrounds have meaningful
                opportunities to participate, build relationships, learn, and succeed.
              </p>
              <p>
                We believe that strong partnerships between families, educators, specialists, and
                the community are essential to children's success. Through open communication,
                collaboration, and mutual respect, we work together to provide consistency and
                support throughout each child's early educational journey.
              </p>
              <p>
                Our mission is to build a strong foundation that prepares children not only for
                their next educational experience, but also to become confident communicators,
                creative thinkers, compassionate friends, independent learners, and active members
                of their communities.
              </p>
              <p className="font-semibold text-brand-green-deep">
                At Innovators Prep, every child belongs, every child has a voice, and every child
                is given the opportunity to learn, grow, and thrive.
              </p>
            </div>

            <h3 className="mt-10 font-display text-2xl md:text-3xl font-bold text-gray-900">
              Nurturing the <span className="text-brand-green">whole child</span>
            </h3>

            <ul className="mt-6 grid sm:grid-cols-1 gap-3" data-testid="goals-list">
              {goals.map((g, i) => (
                <Reveal key={g} delay={i * 0.04}>
                  <li
                    data-testid={`goal-item-${i}`}
                    className="flex items-start gap-3 bg-white rounded-2xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <span className="h-7 w-7 shrink-0 rounded-full bg-brand-green-soft flex items-center justify-center">
                      <CheckCircle2 className="h-4 w-4 text-brand-green-deep" />
                    </span>
                    <span className="text-gray-700 font-medium leading-snug">{g}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1} className="lg:sticky lg:top-28">
            <div className="relative">
              <div className="absolute -inset-6 rounded-[3rem] bg-brand-yellow/20 rotate-2" />
              <img
                src={IMG.goalsImage}
                alt="Group of children laughing together outdoors"
                className="relative rounded-[2.5rem] w-full aspect-[4/5] object-cover shadow-xl"
              />
              <div className="absolute -top-4 -left-4 animate-float">
                <Star color="#9333EA" size={40} />
              </div>
              <div className="absolute -bottom-4 -right-2 animate-float-slow">
                <Star color="#FBBF24" size={48} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
